import { useEffect } from 'react'
import { createRootRoute, createRoute, createRouter, redirect, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { authStore, readAuthConfig, sanitizeReturnTo, useAuth, useSsoSessionWatch, SSO_CALLBACK_PATH } from '@wehunt/core-web'
import { composeStreams } from '@wehunt/app-shell-web'
import { createClubhouseContribution, PinTypeCatalogHost } from '@wehunt/clubhouse-web-wehunt'
import {
  ChronicleTopStats,
  ChronicleOverviewSection,
  ChronicleActivityPanel,
  useChronicleTeams,
} from '@wehunt/chronicle-web-wehunt'
import { outfitterContribution } from '@wehunt/outfitter-web-wehunt'
import { huntDetailSearchFor } from '@wehunt/ui'
import {
  ReplayHuntButton,
  campfirePaths,
  createCampfireContribution,
  huntReplayLocation,
} from '@wehunt/campfire-web-wehunt'
import { SignInPage } from './SignInPage'
import { TeamMapLink } from './TeamMapLink'
import { SignUpPage } from './SignUpPage'
import { SsoRedirectPage } from './SsoRedirectPage'
import { SsoCallbackPage } from './SsoCallbackPage'
import { isFeatureEnabled, shareLinkDomains } from './appConfig'

/**
 * Paths a signed-out visitor may sit on. The callback is one of them even in
 * local mode, where nothing registers the route: this list is the guard's
 * vocabulary, and keeping it mode-independent means the guard reads the same
 * either way.
 */
const AUTH_PATHS = ['/sign-in', '/sign-up', SSO_CALLBACK_PATH]
const isAuthPath = (p: string) => AUTH_PATHS.includes(p)

/**
 * Paths anyone may open, signed in or not, and that never bounce a signed-in
 * visitor home the way the auth pages do.
 *
 * The share link opener is one: the map pins and lines it shows were sent to
 * someone, often someone without an account, and a sign-in wall in front of them
 * would strand every such recipient. It needs no credential itself. The replay
 * pages it navigates to are NOT public, so a signed-out recipient of a replay
 * link still meets the guard there.
 */
const PUBLIC_PATHS: readonly string[] = [campfirePaths.link]
const isPublicPath = (p: string) => PUBLIC_PATHS.includes(p)

/**
 * Whether a path renders inside the app shell (sidebar, top bar, pin catalog).
 *
 * The auth pages and the public paths render bare. For the link opener that holds
 * even when signed in: it is a full-bleed transit screen that is gone as soon as
 * the link resolves, so the shell would add nothing but its own data loading, and
 * keeping one layout for every visitor means the page never remounts mid-resolve
 * when the auth state settles.
 */
export function rendersInShell(pathname: string): boolean {
  return !isAuthPath(pathname) && !isPublicPath(pathname)
}

/**
 * Which login this deployment offers, read once at module load. The runtime
 * config cannot change under a running page, and the route table is built
 * exactly once.
 */
const authMode = readAuthConfig().mode

/**
 * The search a redirect into `/sign-in` carries.
 *
 * Carried in either auth mode: a shared link opened while signed out must
 * survive sign-in, whether sso or the local form performs it. A href that
 * sanitises to `/` is dropped: `/` is where sign-in lands when it has nowhere
 * better, so carrying it is a parameter that changes nothing -- and the
 * Playwright suite asserts the bare `/sign-in` a signed-out visit home produces.
 */
function returnToSearch(href: string): { returnTo?: string } {
  return sanitizeReturnTo(href) === '/' ? {} : { returnTo: href }
}
import { AppShell } from './AppShell'

// Campfire contributes three routes: the participant replay, `/replay/$huntId`;
// `/replay-share/$shareId`, where shared replay links open; and `/link`, the share
// link opener the link service sends computer browsers to, which looks a link's
// code up on the deployment's link service domains and shows a shared pin or line
// itself.
// The team-map link on that page joins campfire to clubhouse, so it is composed
// here.
const campfireContribution = createCampfireContribution({
  shareLinkDomains: shareLinkDomains(),
  renderTeamLink: (groupId) => <TeamMapLink groupId={groupId} />,
})

/**
 * Campfire's "Replay hunt" button, adapted to the shape clubhouse's hunt detail
 * page accepts.
 *
 * This adapter is the whole reason neither domain has to know about the other:
 * campfire's button raises a callback, and the only place that knows both the hunt
 * detail page and campfire's route — here — supplies the navigation. `teamName`
 * rides the query string because the archive has no team on it, and `from` is where
 * Close comes back to.
 */
function HuntReplayHeaderAction({
  huntId,
  teamId,
  teamName,
  huntStartTime,
  attendanceUserIds,
}: {
  huntId: string
  teamId?: string
  teamName?: string
  huntStartTime: string | null
  attendanceUserIds: string[]
}) {
  const navigate = useNavigate()
  return (
    <ReplayHuntButton
      huntStartTime={huntStartTime}
      attendanceUserIds={attendanceUserIds}
      onOpen={() => {
        const from = new URLSearchParams(huntDetailSearchFor({ teamId, status: 'ended' }, '/hunts'))
        const search: Record<string, string> = {
          from: `/hunts/${huntId}?${from.toString()}`,
          source: 'hunt_detail',
        }
        if (teamName !== undefined) search.teamName = teamName
        void navigate({ to: huntReplayLocation(huntId) as '/', search })
      }}
    />
  )
}

// Composition root: clubhouse and chronicle are sibling domains that never
// import each other (RULE-002). The clubhouse overview renders chronicle UI
// inline, so the app injects those slots into the clubhouse contribution.
const clubhouseContribution = createClubhouseContribution(
  {
    TopStats: ChronicleTopStats,
    OverviewSection: ChronicleOverviewSection,
    ActivityPanel: ChronicleActivityPanel,
    useTeams: useChronicleTeams,
  },
  // Flags resolve HERE, in the composition root: appConfig belongs to the shell,
  // and a domain importing it would invert the dependency. The domain receives
  // plain booleans and stays unaware of how they were configured.
  {
    driveSubAreaScope: isFeatureEnabled('driveSubAreaScope'),
    campfireHuntReport: isFeatureEnabled('campfireHuntReport'),
    teamInvite: isFeatureEnabled('teamInvite'),
    huntGuestInvite: isFeatureEnabled('huntGuestInvite'),
  },
  { HuntDetailHeaderAction: HuntReplayHeaderAction },
)

/**
 * Composed shell — a single place that knows about every stream's routes,
 * nav items, and badge hooks. Adding a new stream is a one-line change
 * here plus the stream's own contribution export.
 */
export const shell = composeStreams([
  clubhouseContribution,
  outfitterContribution,
  campfireContribution,
  // future: driveContribution, estateContribution
])

/**
 * Where RootComponent sends a visitor whose session is gone while the page is
 * open, or `undefined` to stay put.
 *
 * A function of its own so the decision can be tested without mounting the
 * router. It carries `returnTo` for the same reason the root guard does: a
 * session that ends on a share page -- a sign-out in another tab, a single
 * logout from sso -- should come back to that page after the next sign-in.
 */
export function signedOutRedirect(
  status: string,
  location: { pathname: string; href: string },
): { to: '/sign-in'; replace: true; search: { returnTo?: string } } | undefined {
  if (status === 'signed-in' || status === 'loading') return undefined
  if (isAuthPath(location.pathname) || isPublicPath(location.pathname)) return undefined
  return { to: '/sign-in', replace: true, search: returnToSearch(location.href) }
}

/** The sign-in and sign-up pages' only search parameter: where to go next. */
const validateAuthSearch = (search: Record<string, unknown>): { returnTo?: string } =>
  typeof search.returnTo === 'string' ? { returnTo: search.returnTo } : {}

function RootComponent() {
  const location = useRouterState({ select: s => s.location })
  const { state } = useAuth()
  const navigate = useNavigate()

  // Single logout: ask the API whether the sso session behind this credential is
  // still alive, on load and whenever the tab comes back. Here rather than in
  // main.tsx because this is the one component that renders for every route and
  // already reads the auth state the watch keys on. Inert outside sso mode.
  useSsoSessionWatch(state.status === 'signed-in' ? state.user.uid : undefined)

  // Reactively send signed-out users on a protected route to /sign-in. Running
  // this in an effect (after React has committed the signed-out state) rather
  // than imperatively at sign-out click time avoids racing the router mid
  // auth-transition, which crashed the app (CLUB-209). Also covers session
  // expiry, not just the explicit sign-out button.
  useEffect(() => {
    const target = signedOutRedirect(state.status, location)
    if (target) void navigate(target)
  }, [state.status, location.pathname, location.href, navigate])

  if (!rendersInShell(location.pathname)) return <Outlet />
  return (
    <PinTypeCatalogHost>
      <AppShell><Outlet /></AppShell>
    </PinTypeCatalogHost>
  )
}

const rootRoute = createRootRoute({
  component: RootComponent,
  beforeLoad: async ({ location }) => {
    // The callback owns its own auth transition: it reads a single-use code
    // from the URL and signs in itself. Waiting on the auth store here would
    // bounce it to '/' whenever a previous session is still cached, and to
    // '/sign-in' while the exchange is in flight -- both before the code has
    // been read, and the code is spent by then.
    if (location.pathname === SSO_CALLBACK_PATH) return
    // Open to everyone, and not an auth page, so a signed-in visitor stays too.
    if (isPublicPath(location.pathname)) return
    await authStore.waitUntilSettled()
    const state = authStore.snapshot()
    const onAuthPage = isAuthPath(location.pathname)
    if ((state.status === 'signed-out' || state.status === 'error') && !onAuthPage) {
      // Where the visitor was heading, so the sign-in can put them back there.
      // `href` is origin-relative, and it is sanitised again before use.
      throw redirect({ to: '/sign-in', search: returnToSearch(location.href) })
    }
    if (state.status === 'signed-in' && onAuthPage) {
      // A signed-in visitor on an auth page still has somewhere to be: a
      // shared link opened in a tab that signed in elsewhere arrives here with
      // its returnTo. The router JSON-parses search values, so the string
      // check is validateAuthSearch's. Sanitised like every other use, so an
      // off-site value lands on the home page in either auth mode.
      const { returnTo } = validateAuthSearch((location.search ?? {}) as Record<string, unknown>)
      throw redirect({ href: sanitizeReturnTo(returnTo) })
    }
  },
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: validateAuthSearch,
  component: authMode === 'sso' ? SsoRedirectPage : SignInPage,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  validateSearch: validateAuthSearch,
  component: authMode === 'sso' ? SsoRedirectPage : SignUpPage,
})

const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SSO_CALLBACK_PATH,
  component: SsoCallbackPage,
})

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  // Registered in sso mode only: in local mode nothing can arrive here, and a
  // route that cannot serve its own purpose should not resolve -- the same
  // reason campfire's replay route is registered behind its flag.
  ...(authMode === 'sso' ? [ssoCallbackRoute] : []),
  ...shell.routes(rootRoute),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
